import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import useLoad from '../utils/useLoad';
import { vpApi, errorMessage, isNotFound, fmtMoney, fmtDateTime, OPP_STAGES, stageOf, VEHICLE_STATUS, vehicleTitle } from '../api/velopulse';

function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: opp, error, loading, setData } = useLoad(() => vpApi.getOpportunity(id), [id]);
  const [moving, setMoving] = useState(false);

  if (loading) return <VpState loading />;
  if (error) {
    return (
      <div className="vp-content">
        <div className="vp-card">
          <VpState
            text={isNotFound(error) ? '找不到這筆商機，或它不屬於目前的組織。' : errorMessage(error, '商機載入失敗。')}
            action={{ label: '回到商機看板', onClick: () => navigate('/opportunities') }}
          />
        </div>
      </div>
    );
  }

  const st = stageOf(opp.stage);

  // 鍵盤／手機也能變更階段（看板拖放的替代操作）
  const changeStage = async (e) => {
    const next = e.target.value;
    const prev = opp.stage;
    setMoving(true);
    setData((d) => ({ ...d, stage: next }));
    try {
      await vpApi.moveOpportunity(opp.id, next);
      toast.success(`已移至「${stageOf(next).label}」`);
    } catch (err) {
      setData((d) => ({ ...d, stage: prev }));
      toast.error(errorMessage(err, '變更階段失敗，已還原。'));
    } finally { setMoving(false); }
  };

  const rows = [
    ['顧客', opp.customer_name],
    ['預估金額', opp.amount != null ? fmtMoney(opp.amount) : null],
    ['來源', opp.source],
    ['負責人', opp.owner],
    ['摘要', opp.summary],
  ];

  return (
    <div className="vp-content">
      <Link className="vp-back" to="/opportunities">← 回到商機看板</Link>
      <div className="vp-detail-head" style={{ marginTop: 8 }}>
        <h1>{opp.code ? `${opp.code} · ` : ''}{opp.title}</h1>
        <span className={`vp-badge ${st.tone}`}>{st.label}</span>
      </div>

      <div className="vp-detail-grid">
        <div className="vp-card">
          <h2>基本資料</h2>
          <div className="vp-fields">
            <div className="vp-kv">
              <label className="k" htmlFor="opp-stage">階段</label>
              <div className="v">
                <select id="opp-stage" className="vp-input" value={opp.stage} onChange={changeStage} disabled={moving}>
                  {OPP_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </div>
            {rows.map(([k, v]) => (
              <div className="vp-kv" key={k} style={k === '摘要' ? { gridColumn: '1 / -1' } : undefined}><div className="k">{k}</div><div className="v">{v || '—'}</div></div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <div className="vp-card">
            <h2>關聯紀錄</h2>
            {opp.record ? (
              <Link className="vp-item" to={`/records/${opp.record.id}`}>
                <span className={`vp-badge ${VEHICLE_STATUS[opp.record.status]?.tone || 'gray'}`}>{VEHICLE_STATUS[opp.record.status]?.label || '車輛'}</span>
                <div><strong>{opp.record.code} · {vehicleTitle(opp.record)}</strong><div className="vp-sub">{[opp.record.model_year, fmtMoney(opp.record.asking_price)].filter(Boolean).join(' · ')}</div></div>
              </Link>
            ) : opp.listing ? (
              <Link className="vp-item" to={`/realty/search`}>
                <span className="vp-badge blue">物件</span>
                <div>
                  <strong>{opp.listing.title}</strong>
                  <div className="vp-sub">{[opp.listing.city, opp.listing.district, opp.listing.price != null ? fmtMoney(opp.listing.price) : null].filter(Boolean).join(' · ')}</div>
                </div>
              </Link>
            ) : <VpState text="這筆商機尚未關聯車輛或物件。同一品項可有多筆不同顧客的商機。" />}
          </div>
          <div className="vp-card">
            <h2>活動時間軸</h2>
            {!(opp.activities || []).length && <VpState text="這筆商機還沒有任何活動。" />}
            <ul className="vp-timeline">
              {(opp.activities || []).map((a) => (
                <li key={a.id}><div>{a.text}</div><div className="vp-muted" style={{ fontSize: 12 }}>{[a.actor, fmtDateTime(a.at)].filter(Boolean).join(' · ')}</div></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OpportunityDetailPage() {
  return <RequireOrg><OpportunityDetail /></RequireOrg>;
}
